import {
  Layout,
  Page,
  Card,
  BlockStack,
  Loading,
  InlineStack,
  Popover,
  Button,
  ActionList,
  SkeletonBodyText,
  Modal,
  TextField,
} from "@medentic/polaris-react";
import TopBanner from "./TopBanner";
import PropertiesCard from "./PropertiesCard";
import { NoteTone } from "@shared/ui";
import PatientNotes from "./PatientNotes";
import AssignDoctor from "./AssignDoctor";
import { TagsCard } from "@shared/ui";
import ProfileImage from "./ProfileImage";
import { AppPageWrapper } from "@shared/ui";
import { useParams, useNavigate } from "react-router-dom";
import usePatientDetails from "@features/clinic-patients/hooks/usePatientDetails";
import { useState, useCallback, useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import PatientDetailTabs from "./PatientDetailTabs";
import { Property } from "../../types";
import TabsContent from "./TabsContent";
import { CLINIC_ROUTES, ROOTS } from "@app/config/constants";
import {
  NoteIcon,
  PrintIcon,
  PlusIcon,
  ImageIcon,
  ChatIcon,
  StatusActiveIcon,
  LinkIcon,
  EditIcon,
  ViewIcon,
  ArchiveIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import usePatients from "@features/clinic-patients/hooks/usePatients";
import { JSONBadgeRenderer } from "@shared/ui";
import usePageMeta from "@shared/hooks/common/usePageMeta";

function PatientDetail() {
  const { t } = useTranslation();
  const [popoverActive, setPopoverActive] = useState(false);
  const [archiveModalActive, setArchiveModalActive] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");

  const togglePopoverActive = useCallback(
    () => setPopoverActive((popoverActive) => !popoverActive),
    []
  );

  const toggleArchiveModal = useCallback(
    () => setArchiveModalActive((active) => !active),
    []
  );

  const handleArchiveReasonChange = useCallback(
    (value: string) => setArchiveReason(value),
    []
  );

  const { clinicId, patientId } = useParams<{
    patientId: string;
    clinicId: string;
  }>();
  const {
    patient,
    isLoading: isLoadingPatient,
    isFetching: isFetchingPatient,
  } = usePatientDetails(patientId ?? "", clinicId ?? "");
  
  const [selectedTab, setSelectedTab] = useState(1);
  const { GET, PUT, DELETE } = useApiRequest();

  const queryClient = useQueryClient();

  // Prepare page meta data
  const patientName = patient ? `${patient.core_firstname || ''} ${patient.core_lastname || ''}`.trim() : undefined;
  const patientAvatar = patient?.core_avatar?.sources?.sort((a, b) => a.width - b.width)[0]?.src 
    || patient?.core_avatar?.value;

  // Update page meta when patient data is available
  usePageMeta(patientName, patientAvatar);

  const { mutate: archivePatientMutation, isPending: isArchivingPatient } =
    useMutation({
      mutationFn: (reason: string) =>
        DELETE(
          `/api/healthcare-companies/${clinicId}/patients/${patientId}`,
          false,
          {
            reason,
            status: "Archived",
          }
        ),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["patientDetails", patientId],
        });
        queryClient.invalidateQueries({ queryKey: ["patients"] });
        navigate(`${ROOTS.CLINIC}/${clinicId}/${CLINIC_ROUTES.PATIENTS}`);
      },
      onError: (error) => {
        console.error("Error archiving patient:", error);
      },
    });

  const { mutate: updateTagMutation } = useMutation({
    mutationFn: (selectedTagIds: string[]) =>
      PUT(`/api/healthcare-companies/${clinicId}/patients/${patientId}/tags`, {
        tagIds: selectedTagIds,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["patientDetails", patientId],
      });
      queryClient.invalidateQueries({ queryKey: ["patients"] });
    },
    onError: (error) => {
      console.error("Error updating tags:", error);
    },
  });

  const {
    data: patientProperties = [],
    isLoading: isLoadingPatientProperties,
  } = useQuery<Property[]>({
    queryKey: ["patient-schema-properties"],
    queryFn: () =>
      GET(`/api/healthcare-companies/${clinicId}/patients/schema/properties`),
    placeholderData: keepPreviousData,
  });

  const handleTagsUpdate = useCallback(
    (selectedTagIds: string[]) => {
      updateTagMutation(selectedTagIds);
    },
    [updateTagMutation]
  );
  const handleTabChange = useCallback((selectedTabIndex: number) => {
    setSelectedTab(selectedTabIndex);
  }, []);

  const navigate = useNavigate();

  const { data: patientsData } = usePatients(clinicId);
  const patients = useMemo(
    () => patientsData?.patients ?? [],
    [patientsData?.patients]
  );

  const currentPatientIndex = patients.findIndex((p) => p.id === patientId);

  const handlePreviousPatient = useCallback(() => {
    if (currentPatientIndex > 0) {
      const previousPatient = patients[currentPatientIndex - 1];
      navigate(`${ROOTS.CLINIC}/${clinicId}/patients/${previousPatient.id}`);
    }
  }, [currentPatientIndex, patients, clinicId, navigate]);

  const handleNextPatient = useCallback(() => {
    if (currentPatientIndex < patients.length - 1) {
      const nextPatient = patients[currentPatientIndex + 1];
      navigate(`${ROOTS.CLINIC}/${clinicId}/patients/${nextPatient.id}`);
    }
  }, [currentPatientIndex, patients, clinicId, navigate]);

  const handleArchivePatient = useCallback(() => {
    setArchiveModalActive(true);
  }, []);

  const handleArchiveConfirm = useCallback(() => {
    if (archiveReason.trim()) {
      archivePatientMutation(archiveReason);
      setArchiveModalActive(false);
      setArchiveReason("");
    }
  }, [archiveReason, archivePatientMutation]);

  return (
    <AppPageWrapper>
      <Page
        title={
          !isLoadingPatient
            ? `${patient?.core_firstname ?? ""} ${patient?.core_lastname ?? ""}`
            : undefined
        }
        titleMetadata={
          typeof patient?.core_status === "object" ? (
            <JSONBadgeRenderer
              badge={patient.core_status}
              patientId={patient.id}
              allowRefresh={true}
            />
          ) : (
            ""
          )
        }
        secondaryActions={
          <InlineStack gap={"200"}>
            <Button icon={PlusIcon}>
              {t("patientDetails.actions.newTreatment")}
            </Button>

            <Button icon={PrintIcon}>
              {t("patientDetails.actions.documents")}
            </Button>
            <Popover
              active={popoverActive}
              activator={
                <Button onClick={togglePopoverActive} disclosure>
                  {t("patientDetails.actions.more")}
                </Button>
              }
              autofocusTarget="first-node"
              onClose={togglePopoverActive}
            >
              <ActionList
                actionRole="menuitem"
                sections={[
                  {
                    items: [
                      {
                        content: t("patientDetails.actions.addImages"),
                        onAction: () => {},
                        icon: ImageIcon,
                      },
                      {
                        content: t("patientDetails.actions.addSMSReminder"),
                        onAction: () => {},
                        icon: ChatIcon,
                      },
                      {
                        content: t("patientDetails.actions.addTask"),
                        onAction: () => {},
                        icon: StatusActiveIcon,
                      },
                      {
                        content: t("patientDetails.actions.addLink"),
                        onAction: () => {},
                        icon: LinkIcon,
                      },
                      {
                        content: t("patientDetails.actions.printPatientFile"),
                        onAction: () => {},
                        icon: PrintIcon,
                      },
                      {
                        content: t("patientDetails.actions.archivePatient"),
                        onAction: handleArchivePatient,
                        icon: ArchiveIcon,
                        destructive: true,
                      },
                    ],
                  },
                  {
                    items: [
                      {
                        content: t(
                          "patientDetails.actions.changePatientFields"
                        ),
                        onAction: () => {},
                        icon: EditIcon,
                      },
                    ],
                  },
                  {
                    title: t("patientDetails.actions.NHIS"),
                    items: [
                      {
                        content: t("patientDetails.actions.viewPatientFile"),
                        onAction: () => {},
                        icon: ViewIcon,
                      },
                      {
                        content: t("patientDetails.actions.newPrescription"),
                        onAction: () => {},
                        icon: NoteIcon,
                      },
                      {
                        content: t("patientDetails.actions.addMedicalNote"),
                        onAction: () => {},
                        icon: NoteIcon,
                      },
                    ],
                  },
                ]}
              />
            </Popover>
          </InlineStack>
        }
        pagination={{
          hasPrevious: currentPatientIndex > 0,
          onPrevious: handlePreviousPatient,
          hasNext: currentPatientIndex < patients.length - 1,
          onNext: handleNextPatient,
        }}
        backAction={{
          content: t("patients.title"),
          url: `${ROOTS.CLINIC}/${clinicId}/${CLINIC_ROUTES.PATIENTS}`,
        }}
      >
        {(isLoadingPatient ||
          isLoadingPatientProperties ||
          isFetchingPatient ||
          isArchivingPatient) && <Loading />}
        <Layout>
          <Layout.Section>
            <BlockStack gap={"400"}>
              <Card>
                {isLoadingPatient || isLoadingPatientProperties ? (
                  <SkeletonBodyText lines={3} />
                ) : patient ? (
                  <TopBanner patient={patient} />
                ) : null}
              </Card>

              <PatientDetailTabs
                selectedTab={selectedTab}
                onTabChange={handleTabChange}
              />
              <TabsContent
                selectedTab={selectedTab}
                patient={patient}
                patientProperties={patientProperties}
                isLoadingPatient={
                  isLoadingPatient || isLoadingPatientProperties
                }
              />
            </BlockStack>
          </Layout.Section>

          <Layout.Section variant="oneThird">
            <BlockStack gap={"400"}>
              <ProfileImage
                avatar={patient?.core_avatar}
                loading={isLoadingPatient}
              />
              <AssignDoctor
                doctorId={patient?.core_patient_owner ?? ""}
                doctorOptions={
                  patientProperties
                    .find((property) => property.name === "core_patient_owner")
                    ?.options?.map((option) => ({
                      value: option.value,
                      label: option.label,
                    })) ?? []
                }
                loading={isLoadingPatient}
              />
              <PatientNotes
                notes={patient?.core_private_note ?? ""}
                tone={
                  (patient?.core_private_note_tone as NoteTone) ?? "default"
                }
                loading={isLoadingPatient}
                patientId={patientId || ""}
                clinicId={clinicId || ""}
                toneOptions={
                  patientProperties
                    .find(
                      (property) => property.name === "core_private_note_tone"
                    )
                    ?.options?.map((option) => ({
                      value: option.value as NoteTone,
                      label: option.label,
                      description: option.description ?? "",
                    })) ?? []
                }
              />

              <PropertiesCard
                properties={patientProperties}
                patient={patient}
                loading={isLoadingPatient}
              />
              <TagsCard
                selectedTags={patient?.tags || []}
                onTagsUpdate={handleTagsUpdate}
                loading={isLoadingPatient}
                hccId={clinicId}
                context="patients"
              />
            </BlockStack>
          </Layout.Section>
        </Layout>
      </Page>
      <Modal
        open={archiveModalActive}
        onClose={toggleArchiveModal}
        title={t("patientDetails.actions.archivePatient")}
        primaryAction={{
          content: t("patientDetails.archiveReason.archive"),
          onAction: handleArchiveConfirm,
          destructive: true,
          disabled: !archiveReason.trim(),
        }}
        secondaryActions={[
          {
            content: t("patientDetails.archiveReason.cancel"),
            onAction: toggleArchiveModal,
          },
        ]}
      >
        <Modal.Section>
          <TextField
            label={t("patientDetails.archiveReason.label")}
            value={archiveReason}
            onChange={handleArchiveReasonChange}
            autoComplete="off"
            multiline={4}
            placeholder={t("patientDetails.archiveReasonPlaceholder")}
            requiredIndicator={true}
          />
        </Modal.Section>
      </Modal>
    </AppPageWrapper>
  );
}

export default PatientDetail;
